/**
 * Route guards (frontend.md §1.1).
 *
 * The rule they all obey: **never redirect while `status === 'hydrating'`.** The
 * boot `POST /auth/refresh` is in flight then, and a guard that treated "not yet
 * known" as "not signed in" would bounce every hard refresh of a deep link to
 * the login screen — and, in M3, tear down a live room connection to do it.
 */

import type { ReactNode } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';

import type { UserRole } from '@ordago/shared';

import { useStore } from '../store';

import { AuthSkeleton } from './AuthSkeleton';

export function RequireAuth({ children }: { children: ReactNode }) {
  const status = useStore((s) => s.auth.status);
  const location = useLocation();

  if (status === 'hydrating') return <AuthSkeleton />;
  if (status === 'anonymous') {
    // Carry the intended destination so the login can return the user to it.
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return <>{children}</>;
}

export function RequireRole({ role, children }: { role: UserRole; children: ReactNode }) {
  const status = useStore((s) => s.auth.status);
  const user = useStore((s) => s.auth.user);

  if (status === 'hydrating') return <AuthSkeleton />;
  if (status === 'anonymous') return <Navigate to="/login" replace />;
  // Not a 403 screen: an address a user may not open should look like an
  // address that is not there.
  if (user?.role !== role) return <Navigate to="/lobby" replace />;
  return <>{children}</>;
}

/** Keeps a signed-in user off the login and register screens. */
export function AnonymousOnly({ children }: { children: ReactNode }) {
  const status = useStore((s) => s.auth.status);
  const [params] = useSearchParams();

  if (status === 'hydrating') return <AuthSkeleton />;
  if (status === 'authenticated') {
    // This guard re-renders the instant the session lands, so it — not the
    // login screen's own `navigate` — is what actually performs the redirect.
    // It has to honour `?next=` or `RequireAuth`'s round trip would always end
    // at the lobby instead of the page the user asked for.
    return <Navigate to={safeNext(params.get('next')) ?? '/lobby'} replace />;
  }
  return <>{children}</>;
}

/**
 * Only same-site paths are followed. Without this, `?next=https://evil.test`
 * would turn the login screen into an open redirect.
 */
export function safeNext(value: string | null): string | undefined {
  if (value === null || !value.startsWith('/') || value.startsWith('//')) return undefined;
  return value;
}
